<template>
  <div class="row">
    <div class="col-12 g">
      <div class="card card-body mb-0">
        <h4 class="mb-25"><i class="fa fa-calendar-check-o"></i> تهيئة العام الجديد</h4>
        <p class="text-muted mb-0">
          في نهاية كل عام: انقل جداول العام الماضي إلى أرشيف ذلك العام، فتبدأ الجداول الحالية فارغة للعام الجديد.
          النقل فوري ولا يضغط قاعدة البيانات مهما كان حجم الجدول (يُعاد تسمية الجدول بالكامل، ولا يُنسخ صف بصف).
          ويمكن استرجاع بيانات أي مدرسة من أرشيف أي عام، أو حذف أرشيف عام قديم كاملاً.
        </p>
      </div>
    </div>

    <div class="col-12 g" v-if="job && job.status == 'running'">
      <div class="alert alert-warning mb-0 p-1">
        <strong>عملية جارية:</strong> {{ jobLabel(job) }}
        <div class="progress mt-50" v-if="job.total">
          <div class="progress-bar" :style="{ width: pct(job.done, job.total) + '%' }"></div>
        </div>
        <small class="num">{{ fmt(job.done) }} <span v-if="job.total">من {{ fmt(job.total) }}</span></small>
      </div>
    </div>

    <!-- 1. Archive -->
    <div class="col-12 col-lg-6 g">
      <div class="card h-100 mb-0">
        <div class="card-header"><h5 class="mb-0"><i class="fa fa-archive"></i> نقل للأرشيف</h5></div>
        <div class="card-body">
          <div class="form-group">
            <label for="arc-year">العام الدراسي المنتهي</label>
            <input id="arc-year" class="form-control" dir="ltr" v-model.trim="year" placeholder="2025-2026">
            <small class="text-muted">بالشكل 2025-2026 أو 2026. كل عام له أرشيف منفصل.</small>
          </div>
          <label>الجداول</label>
          <div class="tables">
            <label v-for="t in tables" :key="t.key" class="tbl" :for="'g-' + t.key">
              <input type="checkbox" :id="'g-' + t.key" :value="t.key" v-model="groups">
              <span class="name">{{ t.label }}</span>
              <span class="num text-muted">{{ fmt(t.count) }} صف</span>
              <small v-if="t.note" class="d-block text-warning">{{ t.note }}</small>
            </label>
          </div>
          <div class="form-group mt-1">
            <label for="arc-confirm">للتأكيد اكتب العام نفسه</label>
            <input id="arc-confirm" class="form-control" dir="ltr" v-model.trim="confirmYear" :placeholder="year">
          </div>
          <button class="btn btn-primary btn-block" :disabled="busy || !groups.length || confirmYear != year" @click="archive()">
            <i class="fa fa-archive"></i> نقل {{ selectedCount ? fmt(selectedCount) + ' صف' : '' }} إلى أرشيف <bdi dir="ltr">{{ year }}</bdi>
          </button>
          <div v-if="lastRun" class="alert alert-success mt-1 mb-0 p-1">
            تم نقل {{ fmt(lastRun.moved) }} صف.
            <small class="d-block" v-if="lastRun.skipped.length">تم تخطي: {{ lastRun.skipped.join('، ') }}</small>
          </div>
        </div>
      </div>
    </div>

    <!-- 2. Years -->
    <div class="col-12 col-lg-6 g">
      <div class="card h-100 mb-0">
        <div class="card-header"><h5 class="mb-0"><i class="fa fa-folder-open"></i> أرشيف السنوات</h5></div>
        <div class="card-body">
          <p v-if="!years.length" class="text-muted">لا يوجد أرشيف بعد.</p>
          <div v-for="y in years" :key="y._id" class="year">
            <div class="d-flex justify-content-between align-items-center">
              <h5 class="mb-0 num" dir="ltr">{{ y.year }}</h5>
              <span>
                <button class="btn btn-sm btn-outline-primary" @click="pickYear(y.year)"><i class="fa fa-undo"></i> استرجاع لمدرسة</button>
                <button class="btn btn-sm btn-outline-danger" @click="deleteYear(y.year)"><i class="fa fa-trash"></i> حذف الأرشيف</button>
              </span>
            </div>
            <small class="text-muted">أُنشئ {{ date(y.created_at) }}</small>
            <ul class="mb-0 mt-25 pr-1">
              <li v-for="g in y.groups" :key="g.key"><small>{{ g.label }}: <span class="num">{{ fmt(g.count) }}</span> صف</small></li>
            </ul>
            <details v-if="y.restores.length" class="mt-25">
              <summary><small>الاسترجاعات ({{ y.restores.length }})</small></summary>
              <small v-for="(r, i) in y.restores" :key="i" class="d-block text-muted">
                {{ date(r.at) }} · {{ r.school }} · {{ fmt(sum(r.moved)) }} صف
              </small>
            </details>
          </div>
        </div>
      </div>
    </div>

    <!-- 3. Restore -->
    <div class="col-12 g" ref="restore">
      <div class="card mb-0">
        <div class="card-header"><h5 class="mb-0"><i class="fa fa-undo"></i> استرجاع لمدرسة</h5></div>
        <div class="card-body">
          <div class="row">
            <div class="col-12 col-lg-4">
              <label for="r-q">المدرسة</label>
              <input id="r-q" class="form-control" v-model="q" @input="searchSchools()" placeholder="ابحث بالاسم أو الجوال">
              <div class="list-group mt-50" v-if="schools.length && !school">
                <a href="javascript:;" class="list-group-item list-group-item-action" v-for="s in schools" :key="s._id" @click="pickSchool(s)">
                  {{ s.name }} <small class="text-muted" dir="ltr">{{ s.phone }}</small>
                </a>
              </div>
              <div v-if="school" class="picked mt-50">
                <strong>{{ school.name }}</strong> · <bdi class="text-muted small" dir="ltr">{{ school.phone }}</bdi> ·
                <a href="javascript:;" @click="school = null; counts = []">تغيير</a>
              </div>
            </div>
            <div class="col-12 col-lg-3">
              <label for="r-year">من أرشيف عام</label>
              <select id="r-year" class="form-control" v-model="restoreYear" @change="loadCounts()">
                <option value="">اختر العام</option>
                <option v-for="y in years" :key="y._id" :value="y.year">{{ ltr(y.year) }}</option>
              </select>
            </div>
            <div class="col-12 col-lg-5">
              <label>الجداول</label>
              <p v-if="!school || !restoreYear" class="text-muted mb-0">اختر المدرسة والعام.</p>
              <div v-else class="tables">
                <label v-for="c in counts" :key="c.key" class="tbl" :for="'r-' + c.key" :class="{ off: !c.restorable || !c.count }">
                  <input type="checkbox" :id="'r-' + c.key" :value="c.key" v-model="restoreGroups" :disabled="!c.restorable || !c.count">
                  <span class="name">{{ c.label }}</span>
                  <span class="num text-muted">{{ c.restorable ? fmt(c.count) + ' صف' : 'لا يُسترجع' }}</span>
                </label>
              </div>
            </div>
          </div>
          <button class="btn btn-success mt-1" :disabled="busy || !school || !restoreYear || !restoreGroups.length" @click="restore()">
            <i class="fa fa-undo"></i> استرجاع {{ fmt(restoreCount) }} صف لـ {{ school ? school.name : 'المدرسة' }}
          </button>
          <small class="text-muted d-block mt-50">يُنقل بدفعات صغيرة في الخلفية دون الضغط على قاعدة البيانات، ويمكن متابعة التقدم أعلى الصفحة. لو توقف لأي سبب، شغّله مرة أخرى ليكمل بدون تكرار.</small>
        </div>
      </div>
    </div>

    <!-- 4. Inactive schools -->
    <div class="col-12 g">
      <div class="card mb-0">
        <div class="card-header">
          <h5 class="mb-0"><i class="fa fa-user-times"></i> مدارس بدون اشتراك منذ ستة أشهر</h5>
          <button class="btn btn-sm btn-outline-secondary" @click="loadInactive()"><i class="fa fa-refresh"></i> تحديث</button>
        </div>
        <div class="card-body">
          <p class="text-muted">
            المدارس التي انتهى آخر اشتراك لها في أي خدمة منذ ستة أشهر أو أكثر (أو لم تشترك أبداً وسجلت منذ ستة أشهر). حساب المدرسة نفسه لا يُحذف.
          </p>
          <p v-if="inactiveTotals" class="summary num">
            <strong>{{ fmt(inactiveTotals.schools) }}</strong> مدرسة ·
            <strong>{{ fmt(inactiveTotals.students) }}</strong> طالب ·
            <strong>{{ fmt(inactiveTotals.teachers) }}</strong> معلم
          </p>
          <div class="table-responsive" v-if="inactive.length">
            <table class="table table-sm table-bordered">
              <thead>
                <tr><th>المدرسة</th><th>آخر اشتراك</th><th>الطلاب</th><th>المعلمون</th><th>التقارير</th><th>حذف</th></tr>
              </thead>
              <tbody>
                <tr v-for="s in inactive" :key="s._id">
                  <td>{{ s.name }}<small class="d-block text-muted" dir="ltr">{{ s.phone }}</small></td>
                  <td class="nowrap"><small>{{ s.never ? 'لم يشترك · سجّل ' : '' }}{{ date(s.since) }}</small></td>
                  <td class="num">{{ fmt(s.students) }}</td>
                  <td class="num">{{ fmt(s.teachers) }}</td>
                  <td class="num">
                    <span v-if="s.reports != null">{{ fmt(s.reports) }}</span>
                    <a v-else href="javascript:;" @click="countReports(s)">احسب</a>
                  </td>
                  <td class="nowrap">
                    <button class="btn btn-sm btn-outline-danger" :disabled="!s.students" @click="purge(s, 'students')">الطلاب</button>
                    <button class="btn btn-sm btn-outline-danger" :disabled="!s.teachers" @click="purge(s, 'teachers')">المعلمين</button>
                    <button class="btn btn-sm btn-outline-danger" :disabled="busy" @click="purge(s, 'reports')">التقارير</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else-if="inactiveTotals" class="text-muted mb-0">لا توجد مدارس بهذه الحالة.</p>
        </div>
      </div>
    </div>

    <!-- 5. Old system -->
    <div class="col-12 g">
      <div class="card mb-0 border-danger">
        <div class="card-header"><h5 class="mb-0 text-danger"><i class="fa fa-trash"></i> التقارير المراد حذفها (النظام القديم _old)</h5></div>
        <div class="card-body">
          <p class="text-muted">جداول النقل القديم التي لم تعد تُستخدم. الحذف نهائي. أرشيف السنوات الجديد لا يظهر هنا.</p>
          <p v-if="!old.length" class="text-muted mb-0">لا توجد جداول قديمة.</p>
          <table class="table table-sm table-bordered mb-0" v-else>
            <thead><tr><th>الجدول</th><th>الصفوف</th><th></th></tr></thead>
            <tbody>
              <tr v-for="o in old" :key="o.name">
                <td dir="ltr" class="text-left">{{ o.name }}</td>
                <td class="num">{{ fmt(o.count) }}</td>
                <td><button class="btn btn-sm btn-danger" @click="dropOld(o)"><i class="fa fa-trash"></i> حذف</button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// The school year that ended this summer (archiving is done between two years).
function defaultYear(){
  var y = new Date().getFullYear();
  return (y - 1) + '-' + y;
}

// Keeps "2025-2026" in order inside Arabic text (plain text: alerts, prompts, confirms).
function ltr(s){
  return '\u2066' + s + '\u2069';
}

export default {
  data() {
    return {
      jwt: localStorage.getItem('jwt'),
      tables: [], groups: [], year: defaultYear(), confirmYear: '', lastRun: null,
      years: [],
      q: '', schools: [], school: null, restoreYear: '', counts: [], restoreGroups: [], searchTimer: null,
      inactive: [], inactiveTotals: null,
      old: [],
      job: null, busy: false, poll: null,
    };
  },
  computed: {
    selectedCount() {
      var g = this;
      return this.tables.filter(function(t){ return g.groups.includes(t.key); }).reduce(function(s, t){ return s + t.count; }, 0);
    },
    restoreCount() {
      var g = this;
      return this.counts.filter(function(c){ return g.restoreGroups.includes(c.key); }).reduce(function(s, c){ return s + c.count; }, 0);
    },
  },
  created() {
    this.loadTables();
    this.loadYears();
    this.loadInactive();
    this.loadOld();
    this.watchJob();
  },
  beforeDestroy() {
    clearInterval(this.poll);
  },
  methods: {
    post(path, body) {
      return $.post(api + '/admin/' + path, Object.assign({ jwt: this.jwt }, body || {}));
    },
    fmt(n) { return (n || 0).toLocaleString('en'); },
    ltr: ltr,
    pct(n, t) { return t ? Math.min(100, Math.round(n * 100 / t)) : 0; },
    sum(o) { return Object.values(o || {}).reduce(function(a, b){ return a + b; }, 0); },
    date(d) { return d ? new Date(d).toLocaleDateString('ar-EG-u-nu-latn', { dateStyle: 'medium' }) : ''; },
    jobLabel(j) {
      if(j.kind == 'restore') return 'استرجاع ' + j.school + ' من أرشيف ' + ltr(j.year);
      if(j.kind == 'purge-reports') return 'حذف تقارير ' + j.school;
      if(j.kind == 'archive') return 'أرشفة ' + ltr(j.year);
      return j.kind;
    },
    loadTables() {
      var g = this;
      this.post('archive/tables').then(function(r){
        if(r.status != 100) return alert(r.response, 200);
        g.tables = r.response;
        if(!g.groups.length) g.groups = r.response.filter(function(t){ return t.checked; }).map(function(t){ return t.key; });
        g.job = r.running;
      });
    },
    loadYears() {
      var g = this;
      this.post('archive/list').then(function(r){ if(r.status == 100) g.years = r.response; });
    },
    loadInactive() {
      var g = this;
      this.post('inactive/list').then(function(r){
        if(r.status != 100) return alert(r.response, 200);
        g.inactive = r.response.map(function(s){ s.reports = null; return s; });
        g.inactiveTotals = r.totals;
      });
    },
    loadOld() {
      var g = this;
      this.post('archive/old').then(function(r){ if(r.status == 100) g.old = r.response; });
    },
    // Follows a background job until it ends, then refreshes what it changed.
    watchJob(id) {
      var g = this;
      clearInterval(this.poll);
      var tick = function(){
        g.post('archive/job', id ? { id: id } : {}).then(function(r){
          g.job = r.response;
          if(!g.job || g.job.status != 'running'){
            clearInterval(g.poll);
            if(id && g.job){
              alert(g.job.status == 'done' ? 'تمت العملية: ' + g.fmt(g.job.done) + ' صف.' : 'توقفت العملية: ' + g.job.error, g.job.status == 'done' ? 100 : 200);
              g.loadTables(); g.loadYears(); g.loadInactive();
              if(g.school && g.restoreYear) g.loadCounts();
            }
          }
        });
      };
      tick();
      this.poll = setInterval(tick, 1500);
    },
    archive() {
      var g = this;
      if(!confirm('نقل الجداول المختارة إلى أرشيف ' + ltr(this.year) + '؟ ستبدأ هذه الجداول فارغة للعام الجديد.')) return;
      g.busy = true;
      this.post('archive/run', { year: this.year, groups: this.groups, confirm: this.confirmYear }).then(function(r){
        g.busy = false;
        if(r.status != 100) return alert(r.response, 200);
        g.lastRun = r.response;
        g.confirmYear = '';
        g.loadTables(); g.loadYears();
      }).fail(function(){ g.busy = false; alert('تعذر الاتصال بالسيرفر', 200); });
    },
    deleteYear(year) {
      var typed = prompt('سيُحذف أرشيف ' + ltr(year) + ' نهائياً من قاعدة البيانات. للتأكيد اكتب العام:');
      if(typed == null) return;
      var g = this;
      this.post('archive/delete-year', { year: year, confirm: typed.trim() }).then(function(r){
        if(r.status != 100) return alert(r.response, 200);
        alert('تم حذف أرشيف ' + ltr(year), 100);
        g.loadYears();
      });
    },
    pickYear(year) {
      this.restoreYear = year;
      this.loadCounts();
      this.$refs.restore.scrollIntoView({ behavior: 'smooth' });
    },
    searchSchools() {
      var g = this;
      this.school = null;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(function(){
        g.post('archive/schools', { q: g.q }).then(function(r){ if(r.status == 100) g.schools = r.response; });
      }, 300);
    },
    pickSchool(s) {
      this.school = s;
      this.loadCounts();
    },
    loadCounts() {
      var g = this;
      this.counts = []; this.restoreGroups = [];
      if(!this.school || !this.restoreYear) return;
      this.post('archive/school-counts', { year: this.restoreYear, user_id: this.school._id }).then(function(r){
        if(r.status != 100) return alert(r.response, 200);
        g.counts = r.response;
        g.restoreGroups = r.response.filter(function(c){ return c.restorable && c.count; }).map(function(c){ return c.key; });
      });
    },
    restore() {
      var g = this;
      if(!confirm('استرجاع ' + this.fmt(this.restoreCount) + ' صف لمدرسة ' + this.school.name + ' من أرشيف ' + ltr(this.restoreYear) + '؟')) return;
      this.post('archive/restore', { year: this.restoreYear, user_id: this.school._id, groups: this.restoreGroups }).then(function(r){
        if(r.status != 100) return alert(r.response, 200);
        g.watchJob(r.response._id);
      });
    },
    countReports(s) {
      var g = this;
      this.post('inactive/reports-count', { user_id: s._id }).then(function(r){
        if(r.status == 100) g.$set(s, 'reports', r.response);
      });
    },
    purge(s, what) {
      var names = { students: 'طلاب', teachers: 'معلمي', reports: 'تقارير ورسائل' };
      var typed = prompt('حذف ' + names[what] + ' مدرسة «' + s.name + '» نهائياً. للتأكيد اكتب: حذف');
      if(typed == null) return;
      var g = this;
      this.post('inactive/delete', { user_id: s._id, what: what, confirm: typed.trim() }).then(function(r){
        if(r.status != 100) return alert(r.response, 200);
        if(r.response.job) return g.watchJob(r.response.job._id);
        alert('تم حذف ' + g.fmt(r.response.deleted) + ' سجل.', 100);
        g.loadInactive();
      });
    },
    dropOld(o) {
      if(!confirm('حذف الجدول ' + o.name + ' (' + this.fmt(o.count) + ' صف) نهائياً؟')) return;
      var g = this;
      this.post('archive/old-delete', { name: o.name }).then(function(r){
        if(r.status != 100) return alert(r.response, 200);
        g.loadOld();
      });
    },
  },
};
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.tables {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 360px;
  overflow-y: auto;
}
.tbl {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 7px 10px;
  border: 1px solid #e3e3e8;
  border-radius: 8px;
  cursor: pointer;
  font-weight: normal;
}
.tbl .name {
  flex: 1;
}
.tbl small {
  flex-basis: 100%;
}
.tbl.off {
  opacity: 0.55;
}
.num {
  font-variant-numeric: tabular-nums;
}
.nowrap {
  white-space: nowrap;
}
.year {
  border-bottom: 1px solid #eee;
  padding: 8px 0;
}
.year:last-child {
  border-bottom: 0;
}
.picked {
  background: #f6f6f9;
  border-radius: 8px;
  padding: 8px 10px;
}
.summary {
  background: #fff8e6;
  border-radius: 8px;
  padding: 8px 12px;
}
.progress {
  height: 6px;
}
</style>
